
import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const TermsPage = () => {
  useEffect(() => {
    // Update page title and meta description for terms of service page
    document.title = 'Terms of Service - WOTECH Information Technologies | Service Agreement';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'WOTECH Terms of Service - Read our service agreement, terms and conditions for using our IT consulting and automation services.');
    }

    // Add structured data for WebPage
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms of Service",
      "description": "WOTECH Terms of Service and service agreement information.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/terms`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Terms of Service",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/terms`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Terms of Service</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Please read these terms and conditions carefully before using WOTECH's services and website.
            </p>
            <div className="mt-8 text-sm text-gray-500">
              Last updated: December 2024
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Agreement to Terms</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These Terms of Service ("Terms") constitute a legally binding agreement between you and WOTECH Information Technologies ("WOTECH," "we," "us," or "our") regarding your use of our website, services, and products.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  By accessing or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our services.
                </p>
              </div>

              {/* Definitions */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Definitions</h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-3">
                  <li><strong>"Services"</strong> refers to all services provided by WOTECH, including but not limited to IT consulting, software implementation, training, and support services.</li>
                  <li><strong>"Website"</strong> refers to our website located at wotech.io and any related subdomains.</li>
                  <li><strong>"User" or "You"</strong> refers to any individual or entity that accesses or uses our services.</li>
                  <li><strong>"Content"</strong> refers to all information, data, text, software, music, sound, photographs, graphics, video, messages, or other materials.</li>
                </ul>
              </div>

              {/* Use of Services */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Use of Our Services</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Permitted Use</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You may use our services for lawful business purposes in accordance with these Terms. You agree to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Provide accurate and complete information when requested</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Use our services in compliance with applicable laws and regulations</li>
                  <li>Respect intellectual property rights</li>
                  <li>Cooperate with our security and authentication measures</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Prohibited Activities</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You agree not to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Use our services for any illegal or unauthorized purpose</li>
                  <li>Attempt to gain unauthorized access to our systems or networks</li>
                  <li>Interfere with or disrupt our services or servers</li>
                  <li>Transmit viruses, malware, or other harmful code</li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Use our services to spam or send unsolicited communications</li>
                </ul>
              </div>

              {/* Service Availability */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Availability</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We strive to maintain high availability of our services, but we do not guarantee uninterrupted access. Our services may be temporarily unavailable due to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Scheduled maintenance and updates</li>
                  <li>Emergency repairs or security measures</li>
                  <li>Third-party service dependencies</li>
                  <li>Force majeure events beyond our control</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  We will make reasonable efforts to provide advance notice of planned maintenance when possible.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Intellectual Property Rights</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Rights</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  All content, features, and functionality of our services, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are owned by WOTECH or our licensors and are protected by copyright, trademark, and other intellectual property laws.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Rights</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license to access and use our services for your internal business purposes. This license does not include the right to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Resell or make commercial use of our services</li>
                  <li>Distribute, publicly perform, or publicly display any part of our services</li>
                  <li>Modify or create derivative works based on our services</li>
                  <li>Reverse engineer or attempt to extract source code</li>
                </ul>
              </div>

              {/* Payment Terms */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Payment Terms</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Fees and Billing</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Service fees are as specified in your service agreement or proposal. Unless otherwise stated:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>All fees are quoted and payable in US Dollars</li>
                  <li>Payment terms are net 30 days from invoice date</li>
                  <li>Late payments may incur interest charges</li>
                  <li>All fees are non-refundable unless otherwise specified</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Taxes</h3>
                <p className="text-gray-600 leading-relaxed">
                  You are responsible for all applicable taxes, duties, and governmental charges related to your use of our services, except for taxes based on our net income.
                </p>
              </div>

              {/* Privacy and Data Protection */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Privacy and Data Protection</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our services, you consent to the collection and use of your information as described in our Privacy Policy.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We implement appropriate security measures to protect your data, but you acknowledge that no method of transmission over the internet is 100% secure.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Limitation of Liability</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To the maximum extent permitted by applicable law, WOTECH shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Loss of profits, data, or business opportunities</li>
                  <li>Business interruption or system downtime</li>
                  <li>Cost of substitute services or products</li>
                  <li>Any damages arising from third-party actions</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Our total liability for any claims arising from or related to these Terms or our services shall not exceed the amount paid by you for the specific service giving rise to the claim during the twelve months preceding the claim.
                </p>
              </div>

              {/* Warranties and Disclaimers */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Warranties and Disclaimers</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Warranty</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We warrant that our services will be performed in a professional and workmanlike manner in accordance with industry standards. If our services fail to meet this warranty, your sole remedy is for us to re-perform the deficient services at no additional cost.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Disclaimers</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Except as expressly stated in these Terms, our services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Warranties of merchantability or fitness for a particular purpose</li>
                  <li>Warranties of non-infringement</li>
                  <li>Warranties that our services will be uninterrupted or error-free</li>
                  <li>Warranties regarding the accuracy or completeness of content</li>
                </ul>
              </div>

              {/* Indemnification */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Indemnification</h2>
                <p className="text-gray-600 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless WOTECH and our officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses arising from or related to your use of our services, violation of these Terms, or infringement of any third-party rights.
                </p>
              </div>

              {/* Termination */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Termination</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Termination by You</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  You may terminate your use of our services at any time by providing written notice. Termination does not relieve you of any payment obligations for services already provided.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Termination by Us</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We may terminate or suspend your access to our services immediately, without prior notice, if you:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Breach any provision of these Terms</li>
                  <li>Fail to make required payments</li>
                  <li>Engage in fraudulent or illegal activities</li>
                  <li>Pose a security risk to our systems or other users</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Effect of Termination</h3>
                <p className="text-gray-600 leading-relaxed">
                  Upon termination, your right to use our services will cease immediately. Provisions that by their nature should survive termination will remain in effect, including payment obligations, intellectual property rights, and limitation of liability.
                </p>
              </div>

              {/* Governing Law */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Governing Law and Dispute Resolution</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Governing Law</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These Terms are governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to conflict of law principles.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Dispute Resolution</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Any disputes arising from or related to these Terms or our services will be resolved through:
                </p>
                <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Negotiation:</strong> Good faith discussions between the parties</li>
                  <li><strong>Mediation:</strong> Non-binding mediation if negotiation fails</li>
                  <li><strong>Arbitration:</strong> Binding arbitration under the rules of the American Arbitration Association</li>
                </ol>
                <p className="text-gray-600 leading-relaxed">
                  You agree to submit to the personal jurisdiction of the courts located in Delaware for any actions not subject to arbitration.
                </p>
              </div>

              {/* General Provisions */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">General Provisions</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Entire Agreement</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These Terms, together with our Privacy Policy and any applicable service agreements, constitute the entire agreement between you and WOTECH regarding the use of our services.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Modifications</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website. Your continued use of our services after such changes constitutes acceptance of the new Terms.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Severability</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If any provision of these Terms is found to be unenforceable or invalid, the remaining provisions will remain in full force and effect.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Waiver</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-4">Assignment</h3>
                <p className="text-gray-600 leading-relaxed">
                  You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms without restriction.
                </p>
              </div>

              {/* Contact Information */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="ri-mail-line text-blue-600 mr-3"></i>
                      <span className="text-gray-700">Email: </span>
                      <a href="mailto:legal@wotech.io" className="text-blue-600 hover:text-blue-700 cursor-pointer ml-1">
                        legal@wotech.io
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-map-pin-line text-blue-600 mr-3"></i>
                      <span className="text-gray-700">Address: WOTECH Information Technologies, Delaware, USA</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line text-blue-600 mr-3"></i>
                      <span className="text-gray-700">Website: </span>
                      <a href="https://wotech.io" className="text-blue-600 hover:text-blue-700 cursor-pointer ml-1">
                        https://wotech.io
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Contact us to learn more about our services and how we can help transform your IT operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Contact Us
              </a>
              <a href="/services" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                View Services
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsPage;